import type { FieldErrors } from 'react-hook-form';
import { type Answers, type AnswersInput, type CurrentIncomeInput, IncomeType } from '../graphql/queries';
import { filteredKeys } from '../constants/applicationsConstants';

export function getAnswersInput(answers: Answers, initialAnswers: Answers): AnswersInput {
  const filteredAnswers = filterAnswersForSubmit(fillAnswerDefaults(answers));
  const filteredInitialAnswers = filterAnswersForSubmit(initialAnswers);

  // Only submit answers that have changed
  return Object.fromEntries(
    Object.entries(filteredAnswers).filter(
      ([key, value]) => JSON.stringify(filteredInitialAnswers[key as keyof AnswersInput]) !== JSON.stringify(value),
    ),
  );
}

function fillAnswerDefaults(answers: Answers): Answers {
  return {
    ...answers,
    // The add/remove document mutations require an incomeId, so we need to
    // add a default income if there are none
    currentIncomes: !answers.currentIncomes?.length
      ? [
          {
            type: IncomeType.employment,
          } satisfies CurrentIncomeInput,
        ]
      : answers.currentIncomes,
  };
}

// Remove __typename and other keys not in AnswersInput as they not accepted in saveAnswers mutation
function filterAnswersForSubmit(answers: Answers): AnswersInput {
  if (!answers) {
    return answers;
  }
  if (Array.isArray(answers)) {
    return answers.map(filterAnswersForSubmit) as AnswersInput;
  }
  return Object.fromEntries(
    Object.entries(answers)
      .filter(([key]) => key !== '__typename' && !filteredKeys.has(key))
      .map(([key, value]) => {
        if (typeof value === 'object') {
          if (key === 'documents') {
            // We don't send documents to the saveAnswers mutation
            // since these are handled by the addDocumentMutation and the removeDocumentMutation
            return [key, null];
          }
          return [key, filterAnswersForSubmit(answers[key as keyof Answers] as Answers)];
        }

        return [key, typeof value === 'string' && value.trim() === '' ? null : value];
      }),
  );
}

export function getAnswersInputWithoutInvalid(
  answers: AnswersInput | Answers,
  initialAnswers?: Answers,
  errors?: FieldErrors<Answers>,
): AnswersInput {
  if (!answers) {
    return answers;
  }
  if (Array.isArray(answers)) {
    return answers.map((answer, index) =>
      getAnswersInputWithoutInvalid(answer, initialAnswers, errors?.[index.toString() as keyof Answers]),
    ) as AnswersInput;
  }
  return Object.fromEntries(
    Object.entries(answers).map(([key, value]) => {
      const fieldId = key as keyof AnswersInput;

      if (value && typeof value === 'object') {
        // recursion
        return [
          key,
          getAnswersInputWithoutInvalid(
            value as AnswersInput,
            initialAnswers ? (initialAnswers[fieldId] as Answers) : undefined,
            errors ? errors[fieldId] : undefined,
          ),
        ];
      }

      if (errors && errors[fieldId]) {
        return [key, initialAnswers ? (initialAnswers[fieldId] as AnswersInput) : null];
      }

      return [key, typeof value === 'string' && value.trim() === '' ? null : value];
    }),
  );
}

export function getErrorsWithoutRequired(errors?: FieldErrors<Answers>): FieldErrors<Answers> {
  if (!errors) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(errors)
      .filter(([_, error]) => !('type' in error) || error.type !== 'required')
      .map(([fieldId, error]) => {
        // recursion for nested error objects like currentResidence.address.zip
        if (!('type' in error)) {
          return [fieldId, getErrorsWithoutRequired(errors[fieldId as keyof Answers])];
        }

        return [fieldId, error];
      })
      .filter(([_, error]) => Object.entries(error || {}).length > 0),
  );
}
